/**
 * Зоны арендаторов на этаже (фаза S8, референс
 * docs/Design/maplibre-3d-building-visualization (3) → InteriorFloor):
 * в режиме «Интерьер» этаж показывается изнутри, и каждый арендатор занимает
 * СВОЮ площадь, а не висит точкой.
 *
 * У нас исходные данные другие: арендатор — это точка (POI) или свой контур
 * (юнит). Поэтому зоны строятся так:
 *  - есть свой контур (юнит внутри здания) — берём его как есть;
 *  - есть только точка — делим свободную площадь этажа между точками
 *    (ближайший арендатор забирает ячейку, диаграмма Вороного по сетке).
 *
 * Чистые функции — тестируются без WebGL.
 */
import type { MPoint } from './model'

export interface TenantZone {
  readonly tenantId: string
  /** Замкнутый контур зоны в метрах (по часовой, как у этажа). */
  readonly outline: readonly MPoint[]
  /** Центр зоны — куда ставится подпись/карточка. */
  readonly center: MPoint
  /** Площадь зоны, м² (показываем в карточке арендатора). */
  readonly areaM2: number
}

/** Прямоугольник в метрах. */
interface Bounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

function boundsOf(outline: readonly MPoint[]): Bounds {
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY
  for (const [x, y] of outline) {
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  }
  return { minX, maxX, minY, maxY }
}

/** Точка внутри контура (ray casting) — ячейки вне этажа не раздаём. */
export function inOutline(pt: MPoint, outline: readonly MPoint[]): boolean {
  let inside = false
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const a = outline[i]
    const b = outline[j]
    if (!a || !b) continue
    if (a[1] > pt[1] !== b[1] > pt[1]) {
      const x = ((b[0] - a[0]) * (pt[1] - a[1])) / (b[1] - a[1]) + a[0]
      if (pt[0] < x) inside = !inside
    }
  }
  return inside
}

/** Площадь простого многоугольника (формула шнурков), м². */
export function polygonArea(outline: readonly MPoint[]): number {
  let sum = 0
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const a = outline[i]
    const b = outline[j]
    if (!a || !b) continue
    sum += (b[0] + a[0]) * (b[1] - a[1])
  }
  return Math.abs(sum / 2)
}

/** Сколько ячеек по длинной стороне: мельче сетка — ровнее границы зон. */
const GRID_STEPS = 26
/** Отступ зоны от соседей, доля ячейки — между зонами виден «коридор». */
const CELL_INSET = 0.14
/**
 * Ячеек на арендатора, к которым стремимся. Фиксированной сетки мало, когда
 * арендаторов много: на палубе судна 44 каюты, и при 26 шагах часть кают не
 * получала ни одной ячейки — то есть исчезала из интерьера.
 */
const CELLS_PER_SEED = 14
/**
 * Потолок числа ячеек. Раздача — O(ячейки × арендаторы), и на слабых телефонах
 * этот проход должен оставаться мгновенным (правило «красиво, но не тормозит»).
 */
const MAX_CELLS = 30_000

/** Шаг сетки: мельче при большом числе арендаторов, но не мельче потолка. */
function gridStep(b: Bounds, seedCount: number): number {
  const w = b.maxX - b.minX
  const h = b.maxY - b.minY
  const coarse = Math.max(w, h) / GRID_STEPS
  const fine = Math.sqrt((w * h) / Math.max(1, seedCount * CELLS_PER_SEED))
  const step = Math.min(coarse, fine)
  const cells = (w / step) * (h / step)
  return cells > MAX_CELLS ? Math.sqrt((w * h) / MAX_CELLS) : step
}

interface Seed {
  readonly id: string
  readonly at: MPoint
}

/**
 * Зоны по точкам арендаторов: сетка внутри контура этажа, каждая ячейка
 * достаётся ближайшей точке. Дальше ячейки одного арендатора объединяются в
 * прямоугольник его владений — простая форма читается лучше «рваного» Вороного
 * и дешевле в отрисовке (одна экструзия на арендатора).
 */
export function zonesFromSeeds(outline: readonly MPoint[], seeds: readonly Seed[]): TenantZone[] {
  if (outline.length < 3 || seeds.length === 0) return []
  if (seeds.length >= DENSE_SEEDS) return denseZones(outline, seeds)
  const b = boundsOf(outline)
  const step = gridStep(b, seeds.length)
  if (!Number.isFinite(step) || step <= 0) return []

  const own = claimCells(outline, seeds, b, step)

  const inset = step * CELL_INSET
  const zones: TenantZone[] = []
  for (const seed of seeds) {
    const o = own.get(seed.id) ?? cellAround(seed)
    const x0 = o.minX - step / 2 + inset
    const x1 = o.maxX + step / 2 - inset
    const y0 = o.minY - step / 2 + inset
    const y1 = o.maxY + step / 2 - inset
    if (x1 <= x0 || y1 <= y0) continue
    const rect: MPoint[] = [
      [x0, y0],
      [x1, y0],
      [x1, y1],
      [x0, y1],
    ]
    zones.push({
      tenantId: seed.id,
      outline: rect,
      center: [(x0 + x1) / 2, (y0 + y1) / 2],
      // Площадь по занятым ячейкам честнее прямоугольника: он описанный.
      areaM2: Math.round(o.cells * step * step),
    })
  }
  return zones
}

/**
 * С этого числа арендаторов на этаже делить всю площадь между ними неправильно:
 * на палубе кают Вороной растягивает зону каждой каюты через весь корпус (её
 * ячейки «утекают» в пустой нос и корму), и план читается как полосы поперёк
 * судна. Плотную раскладку рисуем плитками — каждый занимает свою клетку, между
 * рядами остаётся коридор, как на настоящем плане палубы.
 */
const DENSE_SEEDS = 12
/** Плитка не крупнее этого по стороне — иначе одиночная точка раздувается. */
const DENSE_MAX_HALF_M = 4.5
/** Соседом «по ряду» считаем того, кто почти на одной линии, м. */
const ROW_TOLERANCE_M = 3

/**
 * Зоны-плитки: по каждой оси берём половину расстояния до соседа В ЭТОМ ряду,
 * но не больше лимита. Так каюта получает вытянутую вдоль борта форму, а между
 * рядами остаётся коридор — как на плане палубы, а не «полосы через всё судно».
 */
function denseZones(outline: readonly MPoint[], seeds: readonly Seed[]): TenantZone[] {
  const b = boundsOf(outline)
  const zones: TenantZone[] = []
  for (const seed of seeds) {
    const halfX = halfSide(nearestAlong(seeds, seed, 'x'))
    const halfY = halfSide(nearestAlong(seeds, seed, 'y'))
    const x0 = Math.max(seed.at[0] - halfX, b.minX)
    const x1 = Math.min(seed.at[0] + halfX, b.maxX)
    const y0 = Math.max(seed.at[1] - halfY, b.minY)
    const y1 = Math.min(seed.at[1] + halfY, b.maxY)
    if (x1 <= x0 || y1 <= y0) continue
    zones.push({
      tenantId: seed.id,
      outline: [
        [x0, y0],
        [x1, y0],
        [x1, y1],
        [x0, y1],
      ],
      center: [(x0 + x1) / 2, (y0 + y1) / 2],
      areaM2: Math.round((x1 - x0) * (y1 - y0)),
    })
  }
  return zones
}

/** Половина стороны плитки по расстоянию до соседа (с потолком). */
function halfSide(nearest: number): number {
  if (!Number.isFinite(nearest) || nearest <= 0) return DENSE_MAX_HALF_M
  return Math.min((nearest * 0.92) / 2, DENSE_MAX_HALF_M)
}

/** Расстояние до ближайшего соседа по оси среди стоящих в одном с ним ряду. */
function nearestAlong(seeds: readonly Seed[], from: Seed, axis: 'x' | 'y'): number {
  const i = axis === 'x' ? 0 : 1
  const j = axis === 'x' ? 1 : 0
  let best = Number.POSITIVE_INFINITY
  for (const s of seeds) {
    if (s.id === from.id) continue
    if (Math.abs(s.at[j] - from.at[j]) > ROW_TOLERANCE_M) continue
    best = Math.min(best, Math.abs(s.at[i] - from.at[i]))
  }
  return best
}

/**
 * Запасная зона размером в ячейку вокруг точки: арендатор, которому не досталось
 * ни одной ячейки (стоит вплотную к соседу или у самой стены), всё равно должен
 * быть виден и кликабелен — иначе он молча пропадает из интерьера.
 */
function cellAround(seed: Seed): Bounds & { cells: number } {
  const [x, y] = seed.at
  return { minX: x, maxX: x, minY: y, maxY: y, cells: 1 }
}

/** Раздача ячеек сетки ближайшим арендаторам (границы владений + счётчик). */
function claimCells(
  outline: readonly MPoint[],
  seeds: readonly Seed[],
  b: Bounds,
  step: number,
): Map<string, Bounds & { cells: number }> {
  const own = new Map<string, Bounds & { cells: number }>()
  for (let x = b.minX + step / 2; x <= b.maxX; x += step) {
    for (let y = b.minY + step / 2; y <= b.maxY; y += step) {
      if (!inOutline([x, y], outline)) continue
      const id = nearestSeedId(seeds, x, y)
      const prev = own.get(id)
      if (prev) {
        prev.minX = Math.min(prev.minX, x)
        prev.maxX = Math.max(prev.maxX, x)
        prev.minY = Math.min(prev.minY, y)
        prev.maxY = Math.max(prev.maxY, y)
        prev.cells += 1
      } else {
        own.set(id, { minX: x, maxX: x, minY: y, maxY: y, cells: 1 })
      }
    }
  }
  return own
}

/** Ближайший арендатор к ячейке (квадрат расстояния — корень не нужен). */
function nearestSeedId(seeds: readonly Seed[], x: number, y: number): string {
  let bestId = ''
  let bestD = Number.POSITIVE_INFINITY
  for (const seed of seeds) {
    const dx = seed.at[0] - x
    const dy = seed.at[1] - y
    const d = dx * dx + dy * dy
    if (d < bestD) {
      bestD = d
      bestId = seed.id
    }
  }
  return bestId
}

/** Зона из собственного контура арендатора (юнит внутри здания). */
export function zoneFromOutline(tenantId: string, outline: readonly MPoint[]): TenantZone | null {
  if (outline.length < 3) return null
  const b = boundsOf(outline)
  return {
    tenantId,
    outline,
    center: [(b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2],
    areaM2: Math.round(polygonArea(outline)),
  }
}
